import React from 'react';
import { useFragment } from 'react-relay';
import { graphql } from 'relay-runtime';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import type { notificationPreferencesOptionsWrapper_personalSettingsPreferenceFields_NotificationPreferencesOptions$key } from '@atlassian/jira-relay/src/__generated__/notificationPreferencesOptionsWrapper_personalSettingsPreferenceFields_NotificationPreferencesOptions.graphql';
import { NotifyWhenRoleIsAssignee } from '../../common/ui/assignee-role/index.tsx';
import { EmailEnabledPreferenceField } from '../../common/ui/email-enabled/index.tsx';
import { GroupNotificationEmailsWrapper } from '../../common/ui/group-notification-emails-wrapper/index.tsx';
import MimeTypePreferenceField from '../../common/ui/mime-type/index.tsx';
import { NotifyOwnChangesPreferenceField } from '../../common/ui/notify-own-changes/index.tsx';
import { NotifyWhenRoleIsReporter } from '../../common/ui/reporter-role/index.tsx';
import messages from './messages.tsx';

export const NotificationPreferencesOptions = ({
	emailEnabledFragment,
}: {
	emailEnabledFragment: notificationPreferencesOptionsWrapper_personalSettingsPreferenceFields_NotificationPreferencesOptions$key;
}) => {
	const data = useFragment(
		graphql`
			fragment notificationPreferencesOptionsWrapper_personalSettingsPreferenceFields_NotificationPreferencesOptions on JiraNotificationOptions {
				isEmailNotificationEnabled
				...emailEnabled_personalSettingsPreferenceFields_EmailEnabledPreferenceField
				...assigneeRole_personalSettingsPreferenceFields_NotifyWhenRoleIsAssignee
				...reporterRole_personalSettingsPreferenceFields_NotifyWhenRoleIsReporter
				...notifyOwnChanges_personalSettingsPreferenceFields_NotifyOwnChangesPreferenceField
				...groupNotificationEmailsWrapper_personalSettingsPreferenceFields
				...mimeType_personalSettingsPreferenceFields_MimeTypePreferenceField
			}
		`,
		emailEnabledFragment,
	);
	const { formatMessage } = useIntl();
	return (
		<>
			<Box xcss={containerStyles}>
				<Box
					xcss={[
						headerWrapperStyles,
						data.isEmailNotificationEnabled ? enabledHeaderWrapperStyles : undefined,
					]}
				>
					<EmailEnabledPreferenceField emailEnabledFragment={data} />
				</Box>
				{data.isEmailNotificationEnabled ? (
					<Box
						testId="personal-settings-preference-fields.ui.notification-preferences-options-wrapper.box"
						xcss={[contentWrapperStyles]}
					>
						<Box xcss={preferenceRowStyles}>{formatMessage(messages.wrapperNote)}</Box>
						<Box xcss={preferenceRowStyles}>
							<NotifyWhenRoleIsAssignee assigneeRolePreferenceFragment={data} />
						</Box>
						<Box xcss={preferenceRowStyles}>
							<NotifyWhenRoleIsReporter reporterRolePreferenceFragment={data} />
						</Box>
						<Box xcss={[preferenceRowStyles, preferenceRowLastStyles]}>
							<NotifyOwnChangesPreferenceField notifyOwnChangesEnabledFragment={data} />
						</Box>
					</Box>
				) : null}
			</Box>
			{data.isEmailNotificationEnabled ? (
				<GroupNotificationEmailsWrapper groupNotificationEmailsFragment={data} />
			) : null}
			{data.isEmailNotificationEnabled ? (
				<Box xcss={containerStyles}>
					<Box
						xcss={[
							headerWrapperStyles,
							headerTextStyles,
							emailFormatterWrapperStyles,
							enabledHeaderWrapperStyles,
						]}
					>
						{formatMessage(messages.emailFormatHeading)}
					</Box>
					<Box
						testId="personal-settings-preference-fields.ui.notification-preferences-options-wrapper.box-email-format"
						xcss={[headerWrapperStyles, contentWrapperStyles]}
					>
						<Box xcss={preferenceRowStyles}>{formatMessage(messages.emailFormatNote)}</Box>
						<MimeTypePreferenceField mimeTypeFragment={data} />
					</Box>
				</Box>
			) : null}
		</>
	);
};

const containerStyles = xcss({
	boxSizing: 'border-box',
	marginTop: 'space.100',
	borderRadius: 'border.radius.075',
});

const headerWrapperStyles = xcss({
	borderWidth: 'border.width',
	borderStyle: 'solid',
	borderColor: 'color.border',
	borderRadius: 'border.radius.100',
	paddingRight: 'space.150',
	paddingLeft: 'space.200',
	paddingTop: 'space.100',
	paddingBottom: 'space.100',
});

const headerTextStyles = xcss({
	color: 'color.text',
	fontWeight: token('font.weight.medium'),
});

const enabledHeaderWrapperStyles = xcss({
	borderBottomLeftRadius: '0px',
	borderBottomRightRadius: '0px',
	backgroundColor: 'elevation.surface.sunken',
});

const contentWrapperStyles = xcss({
	backgroundColor: 'elevation.surface',
	borderWidth: 'border.width',
	borderStyle: 'solid',
	borderColor: 'color.border',
	borderRadius: 'border.radius.100',
	borderTop: 'none',
	borderTopLeftRadius: '0px',
	borderTopRightRadius: '0px',
	paddingRight: 'space.200',
	paddingLeft: 'space.200',
	paddingTop: 'space.150',
	paddingBottom: 'space.150',
});

const preferenceRowStyles = xcss({
	marginBottom: 'space.100',
});

const emailFormatterWrapperStyles = xcss({
	paddingTop: 'space.150',
	paddingBottom: 'space.150',
});

const preferenceRowLastStyles = xcss({ marginBottom: 'space.0' });
